import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AppRoutes,
  AUTH_CLIENT_TOKEN,
  AuthClientProvider,
  GameDetailsModel,
  GamesRoutes,
  HostedGamesRoutes,
  MODAL_TOKEN,
  ModalProvider,
  PreventMultiClickDirective,
  USER_DATA_TOKEN,
  UserDataProvider,
  UserModel
} from '@core';
import { Router } from '@angular/router';
import { SendApplicationDirective } from '../../application/directives';
import { SendApplicationCommandHandler } from '../../application/handlers';
import { GameApplicationsService } from '@games/infra';
import { UserParticipationStatus } from '@core-http';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'lib-join-buttons',
  templateUrl: './join-buttons.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SendApplicationDirective, PreventMultiClickDirective, NgTemplateOutlet],
  providers: [SendApplicationCommandHandler, GameApplicationsService]
})
export class JoinButtonsComponent {
  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);
  private readonly authClientProvider: AuthClientProvider = inject(AUTH_CLIENT_TOKEN);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly router: Router = inject(Router);

  readonly game: InputSignal<GameDetailsModel> = input.required<GameDetailsModel>();
  readonly applicationSent: OutputEmitterRef<string> = output<string>();

  readonly isAuthed: Signal<boolean | undefined> = toSignal(this.authClientProvider.isAuthed$);
  private readonly userData: Signal<UserModel | undefined> = toSignal(this.userDataProvider.userData$);

  readonly isMyGame: Signal<boolean> = computed(() => {
    const userData: UserModel | undefined = this.userData();
    const game: GameDetailsModel = this.game();
    return userData?.userId === game.host?.hostId;
  });

  readonly isAppliedGame: Signal<boolean> = computed(() => {
    return this.game().gameParticipationStatus === UserParticipationStatus.Applied;
  });

  readonly isApprovedGame: Signal<boolean> = computed(() => {
    return this.game().gameParticipationStatus === UserParticipationStatus.Approved;
  });

  readonly showJoinButton: Signal<boolean> = computed(() => {
    return !this.isMyGame() && !!this.isAuthed() && !this.isAppliedGame() && !this.isApprovedGame();
  });

  readonly showWaitingButton: Signal<boolean> = computed(() => {
    return !this.isMyGame() && !!this.isAuthed() && this.isAppliedGame();
  });

  readonly showJoinedButton: Signal<boolean> = computed(() => {
    return !this.isMyGame() && !!this.isAuthed() && this.isApprovedGame();
  });

  redirectToAuth(): void {
    this.modalProvider.dismissAllStoredModals();
    this.router.navigate([AppRoutes.AUTH]);
  }

  redirectToGameDetails(): void {
    this.modalProvider.dismissAllStoredModals();
    this.router.navigate([`${AppRoutes.GAMES}/${GamesRoutes.DETAILS}/${this.game().gameId}`]);
  }

  redirectToHostedGameDetails(): void {
    this.modalProvider.dismissAllStoredModals();
    this.router.navigate([`${AppRoutes.HOST}/${HostedGamesRoutes.FUTURE}/${this.game().gameId}`]);
  }

  applicationSentEvent(gameId: string): void {
    this.applicationSent.emit(gameId);
  }
}
